/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {AuthService} from './services/auth.service';
import {environment} from '../../environments/environment';
import {
  ClientErrorApiService,
  InterceptorSkipAuthHeader,
  isOwnApiUrl,
  resolveApiRequestUrl,
} from 'patja-api';
import {HttpErrorTraceService} from './services/http-error-trace.service';
import {Router} from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private httpErrorTraceService: HttpErrorTraceService,
    private clientErrorApiService: ClientErrorApiService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url: string = resolveApiRequestUrl(request.url, environment.apiBaseURL);
    const apiToken: string | null = this.authService.getStoredToken();
    const isExternalAbsoluteUrl = !isOwnApiUrl(url, environment.apiBaseURL);
    const requestWithResolvedUrl: HttpRequest<any> = request.clone({
      url,
      headers: isExternalAbsoluteUrl
        ? request.headers
        : request.headers.set('X-Patja-App', 'client'),
    });

    let requestToHandle: HttpRequest<any>;
    if (this.shouldNotIntercept(request) || isExternalAbsoluteUrl) {
      requestToHandle = requestWithResolvedUrl.clone({
        headers: requestWithResolvedUrl.headers.delete(InterceptorSkipAuthHeader),
        withCredentials: !isExternalAbsoluteUrl,
      });
    } else {
      requestToHandle = this.addApiTokenToHeaders(requestWithResolvedUrl, apiToken || '');
    }

    return next.handle(requestToHandle).pipe(
      filter((event: any) => event instanceof HttpResponse),
      catchError((error: HttpErrorResponse) => {
        this.httpErrorTraceService.capture(error);
        // no login yet, just re-emit the error for processing to continue.
        // we have nothing else to do with this request.
        if (apiToken == null || isExternalAbsoluteUrl) {
          return throwError(() => error);
        } else if (
          error.status === HttpStatusCode.Unauthorized &&
          !this.shouldNotIntercept(request) &&
          !request.headers.has('X-Patja-Auth-Retry')
        ) {
          return this.authService.refreshSession().pipe(
            switchMap(() => {
              const retried = this.addApiTokenToHeaders(
                requestWithResolvedUrl,
                this.authService.getStoredToken() || '',
              );
              return next.handle(
                retried.clone({headers: retried.headers.set('X-Patja-Auth-Retry', '1')}),
              );
            }),
            catchError(() => {
              this.authService.logout();
              return throwError(() => error);
            }),
          );
        } else if (error.status === HttpStatusCode.Unauthorized) {
          this.authService.logout();
          return throwError(() => error);
        }

        if (!isExternalAbsoluteUrl) {
          this.clientErrorApiService.reportHttpError(
            error,
            'client',
            this.router.url,
            request.method,
          );
        }

        return throwError(() => error);
      }),
    );
  }

  private addApiTokenToHeaders(
    request: HttpRequest<any>,
    apiToken: string | null,
  ): HttpRequest<any> {
    const headers: HttpHeaders = request.headers.set('Authorization', `Bearer ${apiToken}`);
    return request.clone({headers, withCredentials: true});
  }

  private shouldNotIntercept(request: HttpRequest<any>): boolean {
    return request.headers.has(InterceptorSkipAuthHeader);
  }
}
